import { useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Link, useNavigate } from 'react-router';
import { Check, CheckCheck, MoreHorizontal } from 'lucide-react';
import {
  type Incident,
  type IncidentStatus,
  useAcknowledgeIncident,
  useDeviceIncidents,
  useResolveIncident,
} from '@/hooks/useIncidents';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { SeverityBadge } from '@/components/SeverityBadge.tsx';

type IncidentFilter = 'all' | 'connection' | 'other';

function isConnectionIncident(incident: Incident): boolean {
  return incident.dedupeKey?.startsWith('connection:') ?? false;
}

const STATUS_STYLE: Record<IncidentStatus, string> = {
  created: 'bg-sev-2/15 text-sev-2',
  acknowledged: 'bg-sev-5/15 text-sev-5',
  resolved: 'bg-sev-4/15 text-sev-4',
  archived: 'bg-muted text-muted-foreground',
};

interface DeviceIncidentsProps {
  deviceId: string;
}

/**
 * Open incidents for a single device. Embedded in DeviceDetails >
 * Incidents tab. Three-tab filter (All / Connection / Other) since
 * connection-flap incidents drown out the rest on noisy devices.
 *
 * Same sev/status palette + row-actions pattern as the main
 * Incidents list — kept consistent so muscle memory transfers.
 */
export default function DeviceIncidents({ deviceId }: DeviceIncidentsProps) {
  const navigate = useNavigate();
  const { data: incidents, isLoading } = useDeviceIncidents(deviceId);
  const acknowledgeMutation = useAcknowledgeIncident();
  const resolveMutation = useResolveIncident();
  const [filter, setFilter] = useState<IncidentFilter>('all');

  const counts = useMemo(() => {
    const all = incidents ?? [];
    const connection = all.filter(isConnectionIncident).length;
    return { all: all.length, connection, other: all.length - connection };
  }, [incidents]);

  const visibleIncidents = useMemo(() => {
    if (!incidents) return [];
    switch (filter) {
      case 'connection':
        return incidents.filter(isConnectionIncident);
      case 'other':
        return incidents.filter((i) => !isConnectionIncident(i));
      default:
        return incidents;
    }
  }, [incidents, filter]);

  if (isLoading) {
    return <Skeleton className="h-32 w-full" />;
  }

  if (!incidents || incidents.length === 0) {
    return (
      <EmptyState
        variant="no-data"
        title="No open incidents"
        description="Nothing to triage on this device."
        size="inline"
      />
    );
  }

  const columns: readonly ResponsiveTableColumn<Incident>[] = [
    {
      key: 'title',
      label: 'Incident',
      hideMobileLabel: true,
      render: (i) => (
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Link
              to={`/incidents/${i.id}`}
              className="font-medium hover:underline truncate"
              onClick={(e) => { e.stopPropagation(); }}
            >
              {i.title}
            </Link>
            {isConnectionIncident(i) ? (
              <Badge variant="secondary" className="text-[10px] font-normal flex-shrink-0">
                Connection
              </Badge>
            ) : null}
          </div>
          {i.description ? (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
              {i.description}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'severity',
      label: 'Severity',
      render: (i) => (
        <SeverityBadge severity={i.severity} />
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (i) => (
        <Badge className={cn('border-0 font-normal capitalize', STATUS_STYLE[i.status])}>
          {i.status}
        </Badge>
      ),
    },
    {
      key: 'source',
      label: 'Source',
      hideAt: 'mobile',
      render: (i) => (
        <span className="text-xs text-muted-foreground">
          {i.source === 'automated' ? 'Auto' : 'Manual'}
        </span>
      ),
    },
    {
      key: 'created',
      label: 'Created',
      hideAt: 'mobile',
      render: (i) => (
        <span className="text-xs text-muted-foreground">
          {formatDistanceToNow(new Date(i.createdAt), { addSuffix: true })}
        </span>
      ),
    },
  ];

  return (
    <div>
      <Tabs
        value={filter}
        onValueChange={(v) => { setFilter(v as IncidentFilter); }}
      >
        <TabsList className="mb-3">
          <TabsTrigger value="all">
            All <span className="text-muted-foreground ml-1">({counts.all})</span>
          </TabsTrigger>
          <TabsTrigger value="connection">
            Connection <span className="text-muted-foreground ml-1">({counts.connection})</span>
          </TabsTrigger>
          <TabsTrigger value="other">
            Other <span className="text-muted-foreground ml-1">({counts.other})</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <ResponsiveTable<Incident>
        columns={columns}
        rows={visibleIncidents}
        rowKey={(r) => r.id}
        onRowClick={(r) => { void navigate(`/incidents/${r.id}`); }}
        rowActions={(incident) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => { e.stopPropagation(); }}
                aria-label={`Actions for ${incident.title}`}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => { e.stopPropagation(); }}>
              {incident.status === 'created' ? (
                <DropdownMenuItem
                  onClick={() => { acknowledgeMutation.mutate(incident.id); }}
                  disabled={
                    acknowledgeMutation.isPending &&
                    acknowledgeMutation.variables === incident.id
                  }
                  className="gap-2"
                >
                  <Check className="w-4 h-4" />
                  Acknowledge
                </DropdownMenuItem>
              ) : null}
              {incident.status === 'created' || incident.status === 'acknowledged' ? (
                <DropdownMenuItem
                  onClick={() => { resolveMutation.mutate(incident.id); }}
                  disabled={
                    resolveMutation.isPending && resolveMutation.variables === incident.id
                  }
                  className="gap-2"
                >
                  <CheckCheck className="w-4 h-4" />
                  Resolve
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        empty={
          <EmptyState
            variant="no-data"
            title="No incidents match this filter"
            size="inline"
          />
        }
      />
    </div>
  );
}
